import { AudioFormat, Language, Voice } from "../../../../typings/types"
import { generateId } from "../../../common/idGenerator"
import { LanguageMap } from "../../manualPage/reducer"
import { BatchItem } from "../BatchItem"
import { InitialErrors, ServerOperationErrors } from "../validation/interfaces"
import { formHasValidationOrOperationErrors } from "../validation/ValidationHelpers"
import { Action, ActionTypes } from "./actions"

export enum Fields {
    SourceVoice = "sourceVoice",
    TargetVoice = "targetVoice",
    FileFormat = "fileFormat"
}

interface FormField {
    value: string
    error: string | boolean
}

interface SelectorField extends FormField {
    value: string
    error: boolean
}

interface AudioFormatOptions {
    [key: number]: AudioFormat
}

type GroupedByFormField<T> = {
    [key in Fields]: T
}

interface IndexableFields<T> {
    fields: GroupedByFormField<T>
}
export interface BatchPageState {
    referenceId: string
    batchItems: BatchItem[]
    allLanguages: LanguageMap
    languageOptions: {
        source: Language[]
        target: Language[]
    }
    voiceOptions: {
        source: Voice[]
        target: Voice[]
    }
    audioFormatOptions: AudioFormatOptions
    sourceLanguage: number
    targetLanguage: number
    firstTranslate: boolean
    fields: {
        [Fields.SourceVoice]: SelectorField
        [Fields.TargetVoice]: SelectorField
        [Fields.FileFormat]: SelectorField
    }
    containsValidationErrors: boolean
    containsOperationErrors: boolean
    originalFilename: string
    serverOperationErrors: ServerOperationErrors
    initialErrors: InitialErrors
}

export const getInitialState = (): BatchPageState => {
    return {
        referenceId: generateId(),
        batchItems: [],
        allLanguages: {},
        languageOptions: {
            source: [],
            target: []
        },
        voiceOptions: {
            source: [],
            target: []
        },
        audioFormatOptions: {},
        sourceLanguage: 0,
        targetLanguage: 0,
        firstTranslate: true,
        fields: {
            [Fields.SourceVoice]: {
                value: "",
                error: false
            },
            [Fields.TargetVoice]: {
                value: "",
                error: false
            },
            [Fields.FileFormat]: {
                value: "",
                error: false
            }
        },
        containsOperationErrors: false,
        containsValidationErrors: false,
        originalFilename: "",
        serverOperationErrors: {},
        initialErrors: {}
    }
}
export function reducer(state: BatchPageState, action: Action): BatchPageState {
    switch (action.type) {
        case ActionTypes.SET_ITEMS:
            return {
                ...state,
                batchItems: action.items,
                containsValidationErrors: action.items.some(item => !item.valid)
            }

        case ActionTypes.UPDATE_ITEM:
            const newItems = state.batchItems.map(item => {
                if (item.index === action.index) {
                    return {
                        ...item,
                        ...action.value
                    }
                }
                return item
            })

            return {
                ...state,
                batchItems: newItems,
                containsValidationErrors: newItems.filter(o => o.include).some(item => !item.valid)
            }

        case ActionTypes.UPDATE_LANGUAGES:
            const languageMap = action.value.reduce((map, lang) => {
                map[lang.id] = lang
                return map
            }, {} as LanguageMap)
            const sourceLanguageOptions = action.value
            const sourceLanguage = sourceLanguageOptions[0].id
            const targetLanguageOptions = sourceLanguageOptions.filter(
                lang => lang.id !== sourceLanguage
            )
            const targetLanguage = targetLanguageOptions[0].id

            return {
                ...state,
                allLanguages: languageMap,
                languageOptions: {
                    source: sourceLanguageOptions,
                    target: targetLanguageOptions
                },
                sourceLanguage: sourceLanguage,
                targetLanguage: targetLanguage,
                voiceOptions: {
                    ...state.voiceOptions,
                    source: languageMap[sourceLanguage].voices,
                    target: languageMap[targetLanguage].voices
                },
                fields: {
                    ...state.fields,
                    [Fields.SourceVoice]: {
                        ...state.fields[Fields.SourceVoice],
                        value: languageMap[sourceLanguage].voices[0].id.toString()
                    },
                    [Fields.TargetVoice]: {
                        ...state.fields[Fields.TargetVoice],
                        value: languageMap[targetLanguage].voices[0].id.toString()
                    }
                }
            }
        case ActionTypes.UPDATE_SELECTOR:
            return {
                ...state,
                fields: {
                    ...state.fields,
                    [action.id]: {
                        ...state.fields[action.id],
                        value: action.value
                    }
                }
            }
        case ActionTypes.UPDATE_AUDIO_FORMATS:
            return {
                ...state,
                audioFormatOptions: action.value.reduce((map, format) => {
                    map[format.id] = format
                    return map
                }, {} as AudioFormatOptions)
            }
        case ActionTypes.UPDATE_SOURCE_LANGUAGE:
            // Update target languages to not include the newly chosen source
            const targetLanguages = Object.values(state.allLanguages).filter(
                lang => lang.id.toString() !== action.value.toString()
            )
            return {
                ...state,
                sourceLanguage: action.value,
                targetLanguage: targetLanguages[0].id,
                languageOptions: {
                    ...state.languageOptions,
                    target: targetLanguages
                },
                voiceOptions: {
                    ...state.voiceOptions,
                    source: state.allLanguages[action.value].voices,
                    target: targetLanguages[0].voices
                },
                fields: {
                    ...state.fields,
                    [Fields.SourceVoice]: {
                        ...state.fields[Fields.SourceVoice],
                        value: state.allLanguages[action.value].voices[0].id.toString()
                    },
                    [Fields.TargetVoice]: {
                        ...state.fields[Fields.TargetVoice],
                        value: targetLanguages[0].voices[0].id.toString()
                    }
                }
            }
        case ActionTypes.UPDATE_TARGET_LANGUAGE:
            return {
                ...state,
                targetLanguage: action.value,
                voiceOptions: {
                    ...state.voiceOptions,
                    target: state.allLanguages[action.value].voices
                },
                fields: {
                    ...state.fields,
                    [Fields.TargetVoice]: {
                        ...state.fields[Fields.TargetVoice],
                        value: state.allLanguages[action.value].voices[0].id.toString()
                    }
                }
            }

        case ActionTypes.UPDATE_FIRST_TRANSLATE:
            return {
                ...state,
                firstTranslate: action.value
            }

        case ActionTypes.SET_ORIGINAL_FILENAME:
            return {
                ...state,
                originalFilename: action.value
            }

        case ActionTypes.SET_OPERATION_ERRORS:
            const newServerOpErrors = {
                ...state.serverOperationErrors
            }
            newServerOpErrors[action.itemIndex] = {
                ...newServerOpErrors[action.itemIndex],
                ...action.errors
            }

            return {
                ...state,
                serverOperationErrors: newServerOpErrors,
                containsOperationErrors: Object.values(newServerOpErrors).some(
                    formHasValidationOrOperationErrors
                )
            }

        case ActionTypes.SET_INITIAL_ERRORS:
            return {
                ...state,
                initialErrors: action.value
            }

        case ActionTypes.RESET:
            return getInitialState()

        default:
            return state
    }
}
